import { useMemo } from 'react'
import { useAdminChurchApprovals, useAdminApproveChurch, useAdminRejectChurch } from '../../hooks/useAdmin'
import { DataTable } from '../../components/admin/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import type { AdminPendingChurch } from '../../hooks/useAdmin'

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function ActionCell({ church }: { church: AdminPendingChurch }) {
  const approve = useAdminApproveChurch()
  const reject = useAdminRejectChurch()
  const busy = approve.isPending || reject.isPending
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => approve.mutate(church.id)}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-[12px] font-semibold border-0 cursor-pointer disabled:opacity-50 font-sans"
      >
        Approve
      </button>
      <button
        type="button"
        onClick={() => {
          const reason = window.prompt('Reason for rejection (optional — shown to the creator):') ?? undefined
          reject.mutate({ churchId: church.id, reason: reason?.trim() || undefined })
        }}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg border border-line text-ink text-[12px] font-medium cursor-pointer disabled:opacity-50 font-sans bg-surface"
      >
        Reject
      </button>
    </div>
  )
}

function useColumns(): ColumnDef<AdminPendingChurch, unknown>[] {
  return useMemo(() => [
    {
      id: 'church',
      accessorFn: (r) => r.name,
      header: 'Church',
      meta: { label: 'Church' },
      cell: ({ row }) => {
        const c = row.original
        const place = [c.city, c.state].filter(Boolean).join(', ')
        return (
          <div>
            <div className="font-medium text-ink">{c.name}</div>
            <div className="text-[12px] text-muted mt-0.5">
              {place || 'Location unknown'} · {c.denomination}
            </div>
          </div>
        )
      },
    },
    {
      id: 'creator',
      accessorFn: (r) => r.ownerDisplayName ?? '',
      header: 'Created by',
      meta: { label: 'Created by' },
      cell: ({ row }) => {
        const c = row.original
        if (!c.ownerEmail && !c.ownerDisplayName) return <span className="text-[12px] text-muted">Unknown</span>
        return (
          <div>
            <div className="text-ink">{c.ownerDisplayName ?? '—'}</div>
            <div className="text-[12px] text-muted">{c.ownerEmail}</div>
          </div>
        )
      },
    },
    {
      id: 'contact',
      accessorFn: (r) => r.websiteUrl ?? r.email ?? '',
      header: 'Church contact',
      meta: { label: 'Church contact' },
      cell: ({ row }) => {
        const c = row.original
        if (!c.websiteUrl && !c.email) return <span className="text-[12px] text-muted">None on file</span>
        return (
          <div className="text-[12px] text-ink-2">
            {c.email && <div>Email: {c.email}</div>}
            {c.websiteUrl && <div>Web: {c.websiteUrl}</div>}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      meta: { label: 'Submitted' },
      cell: ({ getValue }) => <span className="text-muted text-[12px]">{fmt(getValue() as string)}</span>,
      sortingFn: 'datetime',
    },
    {
      id: '_actions',
      header: 'Action',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { skipExport: true },
      cell: ({ row }) => <ActionCell church={row.original} />,
    },
  ], [])
}

export default function AdminChurchApprovalsPage() {
  const { data, isLoading } = useAdminChurchApprovals()
  const churches = useMemo(() => data?.data ?? [], [data])
  const columns = useColumns()

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Church Approvals</h1>
        <p className="text-[13px] text-muted mt-2 m-0 max-w-[560px] leading-[1.5]">
          Churches created by users whose email couldn't be automatically verified against the church's website.
          They stay hidden from search, the map, and public pages until approved. Review and approve or reject each one.
        </p>
        {!isLoading && <div className="text-[13px] text-muted mt-1">{churches.length} pending</div>}
      </div>

      <DataTable
        columns={columns}
        data={churches}
        isLoading={isLoading}
        filterable
        emptyMessage="No churches awaiting approval."
      />
    </div>
  )
}
